import { Controller } from "@hotwired/stimulus"

// Drives the editorial pick form on the admin dashboard: model/snapshot grid
// selection, placement-aware submit enablement, and the debounced live
// preview pane (server-rendered via POST /admin/editorial_picks/preview).
export default class extends Controller<HTMLElement> {
  static targets = [
    "form",
    "modelIdField",
    "snapshotIdField",
    "modelExampleIdField",
    "modelCard",
    "snapshotCard",
    "snapshotHint",
    "snapshotSearch",
    "snapshotModelFilter",
    "snapshotCount",
    "modelPickCard",
    "modelPickSearch",
    "modelPickCount",
    "bodyField",
    "submit",
    "preview",
  ]
  static values = {
    placement: String,
    previewUrl: String,
    pickId: String,
  }

  declare readonly formTarget: HTMLFormElement
  declare readonly modelIdFieldTarget: HTMLInputElement
  declare readonly snapshotIdFieldTarget: HTMLInputElement
  declare readonly modelExampleIdFieldTarget: HTMLInputElement
  declare readonly hasModelExampleIdFieldTarget: boolean
  declare readonly modelCardTargets: HTMLElement[]
  declare readonly snapshotCardTargets: HTMLElement[]
  declare readonly snapshotHintTarget: HTMLElement
  declare readonly snapshotSearchTarget: HTMLInputElement
  declare readonly snapshotModelFilterTarget: HTMLSelectElement
  declare readonly snapshotCountTarget: HTMLElement
  declare readonly hasSnapshotSearchTarget: boolean
  declare readonly hasSnapshotModelFilterTarget: boolean
  declare readonly hasSnapshotCountTarget: boolean
  declare readonly modelPickCardTargets: HTMLElement[]
  declare readonly modelPickSearchTarget: HTMLInputElement
  declare readonly modelPickCountTarget: HTMLElement
  declare readonly hasModelPickSearchTarget: boolean
  declare readonly hasModelPickCountTarget: boolean
  declare readonly bodyFieldTarget: HTMLTextAreaElement
  declare readonly submitTarget: HTMLButtonElement
  declare readonly previewTarget: HTMLElement
  declare readonly hasModelIdFieldTarget: boolean
  declare readonly hasSnapshotIdFieldTarget: boolean
  declare readonly hasSnapshotHintTarget: boolean
  declare readonly hasBodyFieldTarget: boolean
  declare readonly hasSubmitTarget: boolean
  declare readonly hasPreviewTarget: boolean

  declare readonly placementValue: string
  declare readonly previewUrlValue: string
  declare readonly pickIdValue: string

  private previewTimer: number | null = null

  connect() {
    // Snapshot grids of model-scoped placements only show the chosen model's
    // generations; restore that filter when editing.
    if (this.placementValue === "model_highlight") {
      this.filterSnapshotsByModel(this.currentModelId())
    }
    this.markSelection(this.modelCardTargets, this.currentModelId())
    this.markGenerationSelection()
    this.updateSubmitState()
  }

  selectModel(event: Event) {
    event.preventDefault()
    const id = (event.currentTarget as HTMLElement).dataset.id || ""
    if (!this.hasModelIdFieldTarget) return

    this.modelIdFieldTarget.value = id
    this.markSelection(this.modelCardTargets, id)
    if (this.placementValue === "model_highlight") {
      // Switching models clears the featured generation.
      this.setSnapshot("")
      this.markGenerationSelection()
      this.filterSnapshotsByModel(id)
    }
    this.changed()
  }

  selectSnapshot(event: Event) {
    event.preventDefault()
    // The click action sits on the inner button; the id + kind live on the card.
    const card = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-id]")
    const id = card?.dataset.id || ""
    // A generation card is either a challenge snapshot or a curated example
    // (gallery_note). They write to disjoint hidden fields; selecting one
    // clears the other so exactly one generation is ever pinned.
    const kind = card?.dataset.kind || "snapshot"
    if (kind === "example") {
      const next = this.currentModelExampleId() === id ? "" : id
      this.setSnapshot("")
      this.setModelExample(next)
    } else {
      // Optional snapshots (model_highlight) toggle off on re-click.
      const optional = this.placementValue === "model_highlight"
      const next = optional && this.currentSnapshotId() === id ? "" : id
      this.setModelExample("")
      this.setSnapshot(next)
    }
    this.markGenerationSelection()
    this.changed()
  }

  // Freetext + model-select filtering over the rendered snapshot cards
  // (archive_highlight / gallery_note grids).
  filterSnapshots() {
    const query = this.hasSnapshotSearchTarget ? this.snapshotSearchTarget.value.trim().toLowerCase() : ""
    const modelId = this.hasSnapshotModelFilterTarget ? this.snapshotModelFilterTarget.value : ""
    let visible = 0

    this.snapshotCardTargets.forEach((card) => {
      const matchesModel = modelId === "" || card.dataset.modelId === modelId
      const matchesQuery = query === "" || (card.dataset.searchText || "").includes(query)
      const show = matchesModel && matchesQuery
      card.classList.toggle("hidden", !show)
      if (show) visible += 1
    })

    if (this.hasSnapshotCountTarget) {
      const total = this.snapshotCardTargets.length
      this.snapshotCountTarget.textContent = visible === total ? "" : `${visible} of ${total} shown`
    }
  }

  // Freetext filtering over the gallery-note model picker cards (step 2).
  filterModels() {
    const query = this.hasModelPickSearchTarget ? this.modelPickSearchTarget.value.trim().toLowerCase() : ""
    let visible = 0

    this.modelPickCardTargets.forEach((card) => {
      const show = query === "" || (card.dataset.searchText || "").includes(query)
      card.classList.toggle("hidden", !show)
      if (show) visible += 1
    })

    if (this.hasModelPickCountTarget) {
      const total = this.modelPickCardTargets.length
      this.modelPickCountTarget.textContent = visible === total ? "" : `${visible} of ${total} shown`
    }
  }

  changed() {
    this.updateSubmitState()
    this.schedulePreview()
  }

  // ── internals ──

  private currentModelId(): string {
    return this.hasModelIdFieldTarget ? this.modelIdFieldTarget.value : ""
  }

  private currentSnapshotId(): string {
    return this.hasSnapshotIdFieldTarget ? this.snapshotIdFieldTarget.value : ""
  }

  private currentModelExampleId(): string {
    return this.hasModelExampleIdFieldTarget ? this.modelExampleIdFieldTarget.value : ""
  }

  private setSnapshot(id: string) {
    if (!this.hasSnapshotIdFieldTarget) return
    this.snapshotIdFieldTarget.value = id
  }

  private setModelExample(id: string) {
    if (!this.hasModelExampleIdFieldTarget) return
    this.modelExampleIdFieldTarget.value = id
  }

  // Highlight the one generation card matching the pinned snapshot OR example.
  private markGenerationSelection() {
    const snapId = this.currentSnapshotId()
    const exId = this.currentModelExampleId()
    this.snapshotCardTargets.forEach((card) => {
      const kind = card.dataset.kind || "snapshot"
      const id = card.dataset.id || ""
      const selected = kind === "example" ? id !== "" && id === exId : id !== "" && id === snapId
      this.applyCardSelection(card, selected)
    })
  }

  private markSelection(cards: HTMLElement[], id: string) {
    cards.forEach((card) => {
      this.applyCardSelection(card, id !== "" && card.dataset.id === id)
    })
  }

  private applyCardSelection(card: HTMLElement, selected: boolean) {
    card.classList.toggle("border-primary", selected)
    card.classList.toggle("ring-1", selected)
    card.classList.toggle("ring-primary", selected)
    card.classList.toggle("border-base-300", !selected)
    const check = card.querySelector("[data-check]")
    check?.classList.toggle("hidden", !selected)
    check?.classList.toggle("inline-flex", selected)
  }

  private filterSnapshotsByModel(modelId: string) {
    let anyVisible = false
    this.snapshotCardTargets.forEach((card) => {
      const matches = modelId !== "" && card.dataset.modelId === modelId
      card.classList.toggle("hidden", !matches)
      if (matches) anyVisible = true
    })
    if (this.hasSnapshotHintTarget) {
      this.snapshotHintTarget.textContent =
        modelId === ""
          ? "Pick a model above to see its generations."
          : anyVisible
            ? "Optional. Click a generation to feature it; click again to unselect."
            : "No eligible generations for this model."
    }
  }

  private updateSubmitState() {
    if (!this.hasSubmitTarget) return
    const body = this.hasBodyFieldTarget ? this.bodyFieldTarget.value.trim() : ""
    let ready: boolean
    switch (this.placementValue) {
      case "lumenfall_pick":
        ready = this.currentModelId() !== "" && body.length >= 20
        break
      case "model_highlight":
        ready = this.currentModelId() !== "" && body.length > 0
        break
      case "archive_highlight":
        ready = this.currentSnapshotId() !== ""
        break
      case "gallery_note":
      case "outtake":
        ready = (this.currentSnapshotId() !== "" || this.currentModelExampleId() !== "") && body.length > 0
        break
      default: // challenge_highlight
        ready = true
    }
    this.submitTarget.disabled = !ready
  }

  private schedulePreview() {
    if (!this.hasPreviewTarget || this.previewUrlValue === "") return
    if (this.previewTimer) window.clearTimeout(this.previewTimer)
    this.previewTimer = window.setTimeout(() => this.fetchPreview(), 350)
  }

  private async fetchPreview() {
    const data = new FormData(this.formTarget)
    data.delete("_method")
    if (this.pickIdValue !== "") data.set("pick_id", this.pickIdValue)

    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, Accept: "text/html" },
        body: data,
      })
      if (response.ok) this.previewTarget.innerHTML = await response.text()
    } catch {
      // Preview is advisory; never block authoring on it.
    }
  }
}
